import {
  PencilSimple,
  TrashSimple,
  SortAscending,
  SortDescending,
} from "@phosphor-icons/react"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useMemo, memo } from "react"
import Loader from "./Loader"
import Pagination from "./Pagination"
import Filter from "./TableFilters"

const columnHelper = createColumnHelper()

const ErrorCodesMappingsTable = ({
  data,
  loading,
  onPaginationChange,
  pagination,
  pageCount,
  filters,
  setFilters,
  onEdit,
  onDelete,
}) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor("errorCode", {
        header: "Error Code",
        id: "errorCode",
        sortDescFirst: false,
        size: 150,
        meta: {
          filterVariant: "text",
        },
      }),
      columnHelper.accessor("errorDesc", {
        header: "Error Description",
        id: "errorDesc",
        sortDescFirst: false,
        size: 400,
        meta: {
          filterVariant: "text",
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        size: 120,
        cell: ({ row }) => (
          <div className="flex gap-2">
            <button
              className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
              title="Edit"
              onClick={() => onEdit(row.original)}
            >
              <PencilSimple size={16} />
            </button>
            <button
              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
              title="Delete"
              onClick={() => onDelete(row.original)}
            >
              <TrashSimple size={16} />
            </button>
          </div>
        ),
      }),
    ],
    [onEdit, onDelete]
  )

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    onPaginationChange,
    pageCount,
    state: {
      pagination,
    },
  })

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader />
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-200">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-gray-50">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-900"
                    style={{ width: header.getSize() }}
                  >
                    <div className="flex items-center gap-2">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {header.column.getCanSort() && (
                        <button
                          className="flex items-center text-gray-400 hover:text-gray-600"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {header.column.getIsSorted() === "asc" ? (
                            <SortAscending size={16} />
                          ) : header.column.getIsSorted() === "desc" ? (
                            <SortDescending size={16} />
                          ) : (
                            <SortAscending className="opacity-30" size={16} />
                          )}
                        </button>
                      )}
                    </div>
                    {header.column.getCanFilter() ? (
                      <div className="mt-2">
                        <Filter
                          column={header.column}
                          filters={filters}
                          setFilters={setFilters}
                          table={table}
                        />
                      </div>
                    ) : null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="border border-gray-200 px-4 py-3 text-sm text-gray-700"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loading && table.getRowModel().rows.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No error codes found.
        </div>
      )}
      <div className="mt-4">
        <Pagination tableLib={table} />
      </div>
    </div>
  )
}

export default memo(ErrorCodesMappingsTable)
